import * as React from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import {
  ChevronLeft,
  CalendarDays,
  Clock,
  MapPin,
  CreditCard,
  Wallet,
  Banknote,
  CheckCircle2,
  Loader2,
  ShieldCheck,
} from "lucide-react";

import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { markBooked } from "@/lib/reviews-store";
import { formatHoursLabel } from "@/routes/spaces.$spaceId";
import s1 from "@/assets/space-1.webp";
import { Money, Riyal } from "@/components/ui/riyal";

const searchSchema = z.object({
  spaceId: z.string().optional(),
  date: z.string().optional(),
  hours: z.string().optional(), // comma-separated hours
  addons: z.string().optional(), // id:qty,id:qty
});

export const Route = createFileRoute("/checkout")({
  validateSearch: (s) => searchSchema.parse(s),
  component: CheckoutPage,
  head: () => ({
    meta: [
      { name: "robots", content: "noindex, nofollow" },
      { title: "Checkout — Spayzo" },
      { name: "description", content: "Confirm your space booking and complete payment securely." },
    ],
  }),
});

const PRICE_PER_HOUR = 64;
const ADDON_CATALOG: Record<string, { name: string; price: number }> = {
  coffee: { name: "Catering & Coffee Bar", price: 24 },
  photo: { name: "Pro Photographer", price: 80 },
  sound: { name: "Sound & Mic Kit", price: 18 },
  linen: { name: "Premium Linen Setup", price: 12 },
};

type PayMethod = "visa" | "master" | "mada" | "applepay";

const METHODS: {
  id: PayMethod;
  label: string;
  sub: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: string;
}[] = [
  { id: "visa", label: "Visa", sub: "Credit / debit card", icon: CreditCard, badge: "VISA" },
  { id: "master", label: "Mastercard", sub: "Credit / debit card", icon: CreditCard, badge: "MC" },
  { id: "mada", label: "mada", sub: "Saudi debit network", icon: CreditCard, badge: "mada" },
  { id: "applepay", label: "Apple Pay", sub: "Pay with Touch ID / Face ID", icon: Wallet, badge: "" },
];

function CheckoutPage() {
  const { t } = useTranslation();
  const search = Route.useSearch() as z.infer<typeof searchSchema>;
  const navigate = useNavigate();

  const date = React.useMemo(() => (search.date ? new Date(search.date) : new Date()), [search.date]);
  const hours = React.useMemo(
    () =>
      (search.hours ?? "13,14")
        .split(",")
        .map((h) => parseInt(h, 10))
        .filter((n) => !isNaN(n))
        .sort((a, b) => a - b),
    [search.hours],
  );
  const addonItems = React.useMemo(() => {
    const raw = search.addons ?? "";
    return raw
      .split(",")
      .filter(Boolean)
      .map((p) => {
        const [id, qty] = p.split(":");
        return { id, qty: parseInt(qty, 10) || 0 };
      })
      .filter((x) => x.qty > 0 && ADDON_CATALOG[x.id]);
  }, [search.addons]);

  const hoursCount = hours.length;
  const spaceTotal = hoursCount * PRICE_PER_HOUR;
  const addonsTotal = addonItems.reduce((s, a) => s + (ADDON_CATALOG[a.id]?.price ?? 0) * a.qty * hoursCount, 0);
  const serviceFee = Math.round((spaceTotal + addonsTotal) * 0.08);
  const total = spaceTotal + addonsTotal + serviceFee;

  const [name, setName] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [method, setMethod] = React.useState<PayMethod>("visa");
  const [cardNumber, setCardNumber] = React.useState("");
  const [cardExp, setCardExp] = React.useState("");
  const [cardCvc, setCardCvc] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [done, setDone] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const isCardish = method === "visa" || method === "master" || method === "mada";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim() || !phone.trim()) {
      setError(t("checkout.errors.nameAndPhone"));
      return;
    }
    if (isCardish) {
      if (cardNumber.replace(/\s/g, "").length < 12 || cardExp.length < 4 || cardCvc.length < 3) {
        setError(t("checkout.errors.cardDetails"));
        return;
      }
    }
    setSubmitting(true);
    await new Promise((r) => setTimeout(r, 1200));
    if (search.spaceId) markBooked(String(search.spaceId));
    setSubmitting(false);
    setDone(true);
  };

  if (done) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <main className="mx-auto flex max-w-xl flex-col items-center px-4 py-24 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-soft text-primary">
            <CheckCircle2 className="h-9 w-9" />
          </div>
          <h1 className="mt-6 font-display text-3xl font-bold tracking-tight">{t("checkout.bookingConfirmed")}</h1>
          <p className="mt-3 text-muted-foreground">{t("checkout.confirmationSent", { total: `SAR ${total}` })}</p>
          <div className="mt-8 flex gap-3">
            <Button asChild variant="outline" className="rounded-full">
              <Link to="/dashboard">{t("checkout.viewBookings")}</Link>
            </Button>
            <Button asChild className="rounded-full">
              <Link to="/discover">{t("checkout.exploreMore")}</Link>
            </Button>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />

      <main className="mx-auto max-w-7xl px-4 pb-32 pt-6 sm:px-6 lg:px-8 lg:pb-20">
        <button
          onClick={() => navigate({ to: "/spaces/$spaceId", params: { spaceId: search.spaceId ?? "1" } })}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ChevronLeft className="h-4 w-4 rtl:-scale-x-100" /> {t("checkout.backToSpace")}
        </button>

        <div className="mt-4 flex flex-col gap-2">
          <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">{t("checkout.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("checkout.subtitle")}</p>
        </div>

        <form onSubmit={handleSubmit} className="mt-8 grid gap-8 lg:grid-cols-[minmax(0,1fr)_400px]">
          {/* LEFT */}
          <div className="space-y-6">
            {/* Booking summary */}
            <section className="overflow-hidden rounded-3xl border border-border bg-card shadow-soft">
              <div className="flex gap-4 p-5">
                <img src={s1} alt="Space" className="h-24 w-24 shrink-0 rounded-2xl object-cover sm:h-28 sm:w-28" />
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-semibold uppercase tracking-wider text-primary">
                    {t("checkout.yourSpace")}
                  </div>
                  <h2 className="mt-1 font-display text-lg font-semibold leading-snug">{t("checkout.spaceTitle")}</h2>
                  <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                    <MapPin className="h-3 w-3" /> {t("checkout.location")}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Pill icon={CalendarDays}>
                      {date.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
                    </Pill>
                    <Pill icon={Clock}>
                      {hoursCount > 0 ? `${formatHoursLabel(hours)} · ${hoursCount}h` : t("checkout.noTimeSelected")}
                    </Pill>
                  </div>
                </div>
              </div>

              {addonItems.length > 0 && (
                <div className="border-t border-border p-5">
                  <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {t("checkout.addons")}
                  </div>
                  <ul className="mt-3 space-y-2 text-sm">
                    {addonItems.map((a) => {
                      const meta = ADDON_CATALOG[a.id];
                      return (
                        <li key={a.id} className="flex items-center justify-between">
                          <span className="text-foreground">
                            {t(`checkout.addonNames.${a.id}`, { defaultValue: meta.name })}{" "}
                            <span className="text-muted-foreground">
                              × {a.qty} · {hoursCount}h
                            </span>
                          </span>
                          <span className="font-medium">
                            <Riyal /> {meta.price * a.qty * hoursCount}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </section>

            {/* Contact */}
            <section className="rounded-3xl border border-border bg-card p-5 shadow-soft">
              <h2 className="font-display text-lg font-semibold">{t("checkout.yourDetails")}</h2>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="name">{t("checkout.fullName")}</Label>
                  <Input
                    id="name"
                    placeholder={t("checkout.fullNamePlaceholder")}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="phone">{t("checkout.phoneNumber")}</Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder={t("checkout.phonePlaceholder")}
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    required
                  />
                </div>
              </div>
            </section>

            {/* Payment */}
            <section className="rounded-3xl border border-border bg-card p-5 shadow-soft">
              <div className="flex items-center justify-between">
                <h2 className="font-display text-lg font-semibold">{t("checkout.paymentMethod")}</h2>
                <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                  <ShieldCheck className="h-3.5 w-3.5" /> {t("checkout.secured")}
                </span>
              </div>

              <div className="mt-4 grid gap-2 sm:grid-cols-2">
                {METHODS.map((m) => {
                  const active = method === m.id;
                  return (
                    <button
                      type="button"
                      key={m.id}
                      onClick={() => setMethod(m.id)}
                      className={cn(
                        "flex items-center gap-3 rounded-2xl border p-3 text-start transition-all",
                        active
                          ? "border-primary bg-primary-soft shadow-soft"
                          : "border-border bg-card hover:border-primary/40",
                      )}
                    >
                      <span
                        className={cn(
                          "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl",
                          active ? "bg-primary text-primary-foreground" : "bg-muted text-foreground",
                        )}
                      >
                        <m.icon className="h-4 w-4" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{t(`checkout.methods.${m.id}.label`)}</span>
                          {m.badge && (
                            <span className="rounded bg-foreground/5 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-foreground/70">
                              {m.badge}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground">{t(`checkout.methods.${m.id}.sub`)}</div>
                      </div>
                      <span
                        className={cn(
                          "h-4 w-4 shrink-0 rounded-full border-2",
                          active ? "border-primary bg-primary" : "border-border",
                        )}
                      />
                    </button>
                  );
                })}
              </div>

              {isCardish && (
                <div className="mt-5 grid gap-4 rounded-2xl bg-surface p-4 sm:grid-cols-6">
                  <div className="space-y-1.5 sm:col-span-6">
                    <Label htmlFor="card">{t("checkout.cardNumber")}</Label>
                    <Input
                      id="card"
                      inputMode="numeric"
                      placeholder="1234 5678 9012 3456"
                      value={cardNumber}
                      onChange={(e) => setCardNumber(e.target.value)}
                      maxLength={19}
                    />
                  </div>
                  <div className="space-y-1.5 sm:col-span-3">
                    <Label htmlFor="exp">{t("checkout.expiry")}</Label>
                    <Input
                      id="exp"
                      placeholder="08/28"
                      value={cardExp}
                      onChange={(e) => setCardExp(e.target.value)}
                      maxLength={5}
                    />
                  </div>
                  <div className="space-y-1.5 sm:col-span-3">
                    <Label htmlFor="cvc">{t("checkout.cvc")}</Label>
                    <Input
                      id="cvc"
                      inputMode="numeric"
                      placeholder="123"
                      value={cardCvc}
                      onChange={(e) => setCardCvc(e.target.value)}
                      maxLength={4}
                    />
                  </div>
                </div>
              )}

              {method === "applepay" && (
                <p className="mt-4 rounded-2xl bg-surface p-4 text-sm text-muted-foreground">
                  {t("checkout.applePayNote")}
                </p>
              )}
            </section>

            {error && (
              <div className="rounded-2xl border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                {error}
              </div>
            )}
          </div>

          {/* RIGHT — Sticky summary */}
          <aside className="lg:block">
            <div className="lg:sticky lg:top-24">
              <div className="overflow-hidden rounded-3xl border border-border bg-card shadow-elevated">
                <div className="border-b border-border bg-surface p-5">
                  <h3 className="font-display text-lg font-semibold">{t("checkout.orderSummary")}</h3>
                </div>
                <div className="space-y-3 p-5 text-sm">
                  <Row
                    label={t(hoursCount === 1 ? "checkout.priceHours" : "checkout.priceHours_other", {
                      price: `SAR ${PRICE_PER_HOUR}`,
                      count: hoursCount,
                    })}
                    value={<Money value={spaceTotal} />}
                  />
                  {addonItems.length > 0 && (
                    <Row
                      label={`${t("checkout.addons")} (${addonItems.reduce((s, a) => s + a.qty, 0)})`}
                      value={<Money value={addonsTotal} />}
                      muted
                    />
                  )}
                  <Row label={t("checkout.serviceFee")} value={<Money value={serviceFee} />} muted />
                  <div className="flex items-baseline justify-between border-t border-border pt-4">
                    <span className="font-display text-base font-semibold">{t("checkout.total")}</span>
                    <span className="font-display text-2xl font-bold">
                      <Riyal /> {total}
                    </span>
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    disabled={submitting || hoursCount === 0}
                    className="hidden w-full rounded-full shadow-glow lg:inline-flex"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" /> {t("checkout.processing")}
                      </>
                    ) : (
                      <>
                        {t("checkout.confirmBooking")} · <Riyal /> {total}
                      </>
                    )}
                  </Button>
                  <p className="hidden text-center text-xs text-muted-foreground lg:block">
                    {t("checkout.agreeTerms")}
                  </p>
                </div>
              </div>
            </div>
          </aside>

          {/* Mobile sticky CTA */}
          <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-card/95 p-3 backdrop-blur-xl shadow-elevated lg:hidden">
            <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-1">
              <div>
                <div className="font-display text-lg font-bold leading-none">
                  <Riyal /> {total}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">Total</div>
              </div>
              <Button
                type="submit"
                size="lg"
                disabled={submitting || hoursCount === 0}
                className="rounded-full px-6 shadow-glow"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : t("checkout.confirmBooking")}
              </Button>
            </div>
          </div>
        </form>
      </main>

      <SiteFooter />
    </div>
  );
}

function Pill({
  icon: Icon,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface px-3 py-1 text-xs font-medium text-foreground">
      <Icon className="h-3.5 w-3.5 text-muted-foreground" />
      {children}
    </span>
  );
}

function Row({ label, value, muted }: { label: string; value: React.ReactNode; muted?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className={cn("truncate", muted ? "text-muted-foreground" : "text-foreground")}>{label}</span>
      <span className={cn("font-medium", muted ? "text-muted-foreground" : "text-foreground")}>{value}</span>
    </div>
  );
}

function formatHour(h: number) {
  const hour = h % 24;
  const suffix = hour >= 12 ? "PM" : "AM";
  const display = hour % 12 === 0 ? 12 : hour % 12;
  return `${display} ${suffix}`;
}
